import React, { useState } from "react";
import Modal from "apollo-react/components/Modal";
import Typography from "apollo-react/components/Typography";
import FileUpload from "apollo-react/components/FileUpload";
import TextField from "apollo-react/components/TextField";
import { useFileUpload } from "../../../hooks/useFileUpload";
import { toast } from "react-toastify";

const CertificateUploadModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  isProcessing,
  requestName 
}) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [licenseNumber, setLicenseNumber] = useState("");
  const [licenseNumberError, setLicenseNumberError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  
  // Use admin upload endpoint
  const { uploadFile } = useFileUpload(true);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Only PDF and image files (JPG, PNG) are allowed', {
        position: 'top-center',
      });
      return;
    }

    try {
      setIsUploading(true);
      const uploadedFileData = await uploadFile(file);
      
      if (uploadedFileData) {
        setUploadedFile(uploadedFileData);
      }
    } catch (error) {
      console.error('Error uploading certificate:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileDelete = () => {
    setUploadedFile(null);
  };

  const handleLicenseNumberChange = (value) => {
    setLicenseNumber(value);
    
    // Clear error when user starts typing
    if (licenseNumberError) {
      setLicenseNumberError("");
    }
    
    // Validate format in real-time
    if (value && !/^[a-zA-Z0-9\s-]*$/.test(value)) {
      setLicenseNumberError("Only letters, numbers, spaces, and hyphens are allowed");
    }
  };

  const validateLicenseNumber = () => {
    const trimmedLicense = licenseNumber.trim();
    
    if (!trimmedLicense) {
      setLicenseNumberError("Certificate license number is required");
      return false;
    }
    
    if (!/^[a-zA-Z0-9\s-]+$/.test(trimmedLicense)) {
      setLicenseNumberError("Only letters, numbers, spaces, and hyphens are allowed");
      return false;
    }
    
    if (trimmedLicense.length > 255) {
      setLicenseNumberError("License number must be less than 255 characters");
      return false;
    }
    
    return true;
  };

  const handleConfirm = () => {
    // Validate file upload
    if (!uploadedFile) {
      toast.error('Please upload certificate', {
        position: 'top-center',
      });
      return;
    }
    
    // Validate license number
    if (!validateLicenseNumber()) {
      return;
    }
    
    // Pass both file ID and license number to the confirm handler
    onConfirm(uploadedFile.id, licenseNumber.trim());
  };

  const handleClose = () => {
    if (!isProcessing && !isUploading) {
      // Reset state when closing
      setUploadedFile(null);
      setLicenseNumber("");
      setLicenseNumberError("");
      onClose();
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title="Upload Drug Certificate"
      size="medium"
      buttonProps={[
        {
          label: 'Cancel',
          onClick: handleClose,
          disabled: isProcessing || isUploading
        },
        {
          label: isProcessing ? 'Submitting...' : 'Submit',
          onClick: handleConfirm,
          disabled: isProcessing || isUploading || !uploadedFile || !licenseNumber.trim(),
          variant: 'primary',
          status: 'success'
        }
      ]}
    >
      <div style={{ padding: "20px" }}>
        <Typography
          variant="bodyDefault"
          style={{ color: "#333", marginBottom: "24px", textAlign: "center" }}
        >
          In order to approve this request <strong>{requestName}</strong> to be registered as new drug, please upload the certificate.
        </Typography>
        
        <FileUpload
          value={uploadedFile ? [uploadedFile] : []}
          onUpload={handleFileUpload}
          onFileDelete={handleFileDelete}
          maxItems={1}
          fullWidth={true}
          disabled={isProcessing || isUploading}
          accept=".pdf,.jpg,.jpeg,.png"
          label="Upload Certificate (PDF or Image)"
          helperText="Accepted formats: PDF, JPG, PNG"
        />
        
        {isUploading && (
          <Typography
            variant="bodySmall"
            style={{ color: "#666", marginTop: "12px", textAlign: "center" }}
          >
            Uploading certificate...
          </Typography>
        )}
        
        <div style={{ marginTop: "20px" }}>
          <TextField
            label={
              <span>
                Certificate License Number <span style={{ color: "#d32f2f" }}>*</span>
              </span>
            }
            placeholder="Add certificate license number"
            value={licenseNumber}
            onChange={(e) => handleLicenseNumberChange(e.target.value)}
            disabled={isProcessing || isUploading}
            fullWidth
            error={!!licenseNumberError}
            helperText={
              licenseNumberError || 
              "Only letters, numbers, spaces, and hyphens are allowed"
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default CertificateUploadModal;
